import {type RecordOf, Record, List} from 'immutable';

import {
  defaultAffectedElement,
  defaultEventTargetConsts,
  defaultEventConfigConsts,
  defaultEventActionConsts,
  // Event records
  EventRecords as EventPojoRecords,
} from '@packages/systems/ix2/designer/records/pojo';

import {EventTypeConsts} from '@packages/systems/ix2/shared-constants';

import type {
  StyleBlockId,
  AffectedElementType,
} from '@packages/systems/ix2/types-core';

/**
 * Event Records
 */

export const SelectorRecord: Record.Factory<{
  selectorGuids: List<StyleBlockId>;
}> = Record({selectorGuids: List()}, 'SelectorRecord');

// @ts-expect-error - TS2322 - Type 'Factory<AffectedElementType>' is not assignable to type 'Factory<AffectedElementType & { selector: (Record<{ selectorGuids: List<string>; }> & Readonly<{ selectorGuids: List<string>; }>) | undefined; }>'.
export const AffectedElementRecord: Record.Factory<
  AffectedElementType & {
    selector: undefined | ReturnType<typeof SelectorRecord>;
  }
> = Record(defaultAffectedElement, 'AffectedElementRecord');

export const EventTargetRecordConsts: {
  ELEMENT: Record.Factory<
    typeof defaultEventTargetConsts.ELEMENT & {
      styleBlockIds: List<StyleBlockId>;
    }
  >;
  PAGE: Record.Factory<
    typeof defaultEventTargetConsts.PAGE & {
      styleBlockIds: List<StyleBlockId>;
    }
  >;
  CLASS: Record.Factory<
    typeof defaultEventTargetConsts.CLASS & {
      styleBlockIds: List<StyleBlockId>;
    }
  >;
} = {
  // @ts-expect-error - TS2322 - Type 'Factory<{ styleBlockIds: List<string>; id: string | null; appliesTo: "CLASS" | "ELEMENT"; }>' is not assignable to type 'Factory<EventTargetElementType & { styleBlockIds: List<string>; }>'.
  ELEMENT: Record(
    {
      ...defaultEventTargetConsts.ELEMENT,
      styleBlockIds: List(),
    },
    'EventTargetElementRecord'
  ),

  // @ts-expect-error - TS2322 - Type 'Factory<{ styleBlockIds: List<string>; id: string | null; appliesTo: "PAGE"; }>' is not assignable to type 'Factory<EventTargetPageType & { styleBlockIds: List<string>; }>'.
  PAGE: Record(
    {
      ...defaultEventTargetConsts.PAGE,
      styleBlockIds: List(),
    },
    'EventTargetPageRecord'
  ),

  // @ts-expect-error - TS2322 - Type 'Factory<{ styleBlockIds: List<string>; id: string | null; appliesTo: "CLASS" | "ELEMENT"; }>' is not assignable to type 'Factory<EventTargetElementType & { styleBlockIds: List<string>; }>'.
  CLASS: Record(
    {
      ...defaultEventTargetConsts.CLASS,
      styleBlockIds: List(),
    },
    'EventTargetClassRecord'
  ),
};

export const EventConfigRecordConsts: {
  TIMED: Record.Factory<typeof defaultEventConfigConsts.TIMED>;
  CONTINUOUS: {
    MOUSE: {
      ELEMENT: {
        X_AXIS: Record.Factory<
          typeof defaultEventConfigConsts.CONTINUOUS.MOUSE.ELEMENT.X_AXIS
        >;
        Y_AXIS: Record.Factory<
          typeof defaultEventConfigConsts.CONTINUOUS.MOUSE.ELEMENT.Y_AXIS
        >;
      };
      VIEWPORT: {
        X_AXIS: Record.Factory<
          typeof defaultEventConfigConsts.CONTINUOUS.MOUSE.VIEWPORT.X_AXIS
        >;
        Y_AXIS: Record.Factory<
          typeof defaultEventConfigConsts.CONTINUOUS.MOUSE.VIEWPORT.Y_AXIS
        >;
      };
    };
    SCROLL: Record.Factory<typeof defaultEventConfigConsts.CONTINUOUS.SCROLL>;
  };
} = {
  TIMED: Record(defaultEventConfigConsts.TIMED, 'EventConfigTimedRecord'),

  CONTINUOUS: {
    MOUSE: {
      ELEMENT: {
        X_AXIS: Record(
          defaultEventConfigConsts.CONTINUOUS.MOUSE.ELEMENT.X_AXIS,
          'EventConfigContinuousElementXAxisRecord'
        ),
        Y_AXIS: Record(
          defaultEventConfigConsts.CONTINUOUS.MOUSE.ELEMENT.Y_AXIS,
          'EventConfigContinuousElementYAxisRecord'
        ),
      },

      VIEWPORT: {
        X_AXIS: Record(
          defaultEventConfigConsts.CONTINUOUS.MOUSE.VIEWPORT.X_AXIS,
          'EventConfigContinuousViewportXAxisRecord'
        ),
        Y_AXIS: Record(
          defaultEventConfigConsts.CONTINUOUS.MOUSE.VIEWPORT.Y_AXIS,
          'EventConfigContinuousViewportYAxisRecord'
        ),
      },
    },

    SCROLL: Record(
      defaultEventConfigConsts.CONTINUOUS.SCROLL,
      'EventConfigContinuousScrollRecord'
    ),
  },
};

export const EventActionRecordConsts: {
  TIMED: () => RecordOf<
    typeof defaultEventActionConsts.TIMED & {
      config: () => RecordOf<typeof defaultEventActionConsts.TIMED.config>;
    }
  >;
  CONTINUOUS: () => RecordOf<
    typeof defaultEventActionConsts.CONTINUOUS & {
      config: () => RecordOf<typeof defaultEventActionConsts.CONTINUOUS.config>;
    }
  >;
} = {
  TIMED: () =>
    // @ts-expect-error - TS2322 - Type 'Record<{ config: Record<TimedEventActionConfigType> & Readonly<TimedEventActionConfigType>; id: string; actionTypeId: "" | ... 21 more ... | null; instant: boolean; }> & Readonly<...>' is not assignable to type 'RecordOf<TimedEventActionType & { config: () => RecordOf<TimedEventActionConfigType>; }>'.
    Record({
      ...defaultEventActionConsts.TIMED,
      config: Record(
        defaultEventActionConsts.TIMED.config,
        'EventActionTimedRecord'
      )(),
    })(),

  CONTINUOUS: () =>
    // @ts-expect-error - TS2322 - Type 'Record<{ config: Record<ContinuousEventActionConfigType> & Readonly<ContinuousEventActionConfigType>; id: string; actionTypeId: "GENERAL_CONTINUOUS_ACTION"; instant: true; }> & Readonly<...>' is not assignable to type 'RecordOf<ContinuousEventActionType & { config: () => RecordOf<ContinuousEventActionConfigType>; }>'.
    Record({
      ...defaultEventActionConsts.CONTINUOUS,
      config: Record(
        defaultEventActionConsts.CONTINUOUS.config,
        'EventActionContinuousRecord'
      )(),
    })(),
};

const defaultTimedEventConfigRecord = EventConfigRecordConsts.TIMED();
const defaultTimedScrollEventConfigRecord = EventConfigRecordConsts.TIMED({
  scrollOffsetUnit: '%',
  scrollOffsetValue: 0,
});
const defaultTargetElementRecord = EventTargetRecordConsts.ELEMENT();
const defaultTargetPageRecord = EventTargetRecordConsts.PAGE();

/**
 * Timed Events Immutable
 */

const DropdownOpenEventRecord = Record(
  {
    ...EventPojoRecords.DROPDOWN_OPEN,
    origin: undefined,
    config: defaultTimedEventConfigRecord,
    target: defaultTargetElementRecord,
    targets: List([defaultTargetElementRecord]),
  },
  'DropdownOpenEventRecord'
);
const DropdownCloseEventRecord = Record(
  {
    ...EventPojoRecords.DROPDOWN_CLOSE,
    origin: undefined,
    config: defaultTimedEventConfigRecord,
    target: defaultTargetElementRecord,
    targets: List([defaultTargetElementRecord]),
  },
  'DropdownCloseEventRecord'
);
const NavbarOpenEventRecord = Record(
  {
    ...EventPojoRecords.NAVBAR_OPEN,
    origin: undefined,
    config: defaultTimedEventConfigRecord,
    target: defaultTargetElementRecord,
    targets: List([defaultTargetElementRecord]),
  },
  'NavbarOpenEventRecord'
);
const NavbarCloseEventRecord = Record(
  {
    ...EventPojoRecords.NAVBAR_CLOSE,
    origin: undefined,
    config: defaultTimedEventConfigRecord,
    target: defaultTargetElementRecord,
    targets: List([defaultTargetElementRecord]),
  },
  'NavbarCloseEventRecord'
);
const SliderActiveEventRecord = Record(
  {
    ...EventPojoRecords.SLIDER_ACTIVE,
    origin: undefined,
    config: defaultTimedEventConfigRecord,
    target: defaultTargetElementRecord,
    targets: List([defaultTargetElementRecord]),
  },
  'SliderActiveEventRecord'
);
const SliderInactiveEventRecord = Record(
  {
    ...EventPojoRecords.SLIDER_INACTIVE,
    origin: undefined,
    config: defaultTimedEventConfigRecord,
    target: defaultTargetElementRecord,
    targets: List([defaultTargetElementRecord]),
  },
  'SliderInactiveEventRecord'
);
const TabActiveEventRecord = Record(
  {
    ...EventPojoRecords.TAB_ACTIVE,
    origin: undefined,
    config: defaultTimedEventConfigRecord,
    target: defaultTargetElementRecord,
    targets: List([defaultTargetElementRecord]),
  },
  'TabActiveEventRecord'
);
const TabInactiveEventRecord = Record(
  {
    ...EventPojoRecords.TAB_INACTIVE,
    origin: undefined,
    config: defaultTimedEventConfigRecord,
    target: defaultTargetElementRecord,
    targets: List([defaultTargetElementRecord]),
  },
  'TabInactiveEventRecord'
);
const MouseClickFirstEventRecord = Record(
  {
    ...EventPojoRecords.MOUSE_CLICK,
    origin: undefined,
    config: defaultTimedEventConfigRecord,
    target: defaultTargetElementRecord,
    targets: List([defaultTargetElementRecord]),
  },
  'MouseClickFirstEventRecord'
);
const MouseClickSecondEventRecord = Record(
  {
    ...EventPojoRecords.MOUSE_SECOND_CLICK,
    origin: undefined,
    config: defaultTimedEventConfigRecord,
    target: defaultTargetElementRecord,
    targets: List([defaultTargetElementRecord]),
  },
  'MouseClickSecondEventRecord'
);
const MouseHoverOverEventRecord = Record(
  {
    ...EventPojoRecords.MOUSE_OVER,
    origin: undefined,
    config: defaultTimedEventConfigRecord,
    target: defaultTargetElementRecord,
    targets: List([defaultTargetElementRecord]),
  },
  'MouseHoverOverEventRecord'
);
const MouseHoverOutEventRecord = Record(
  {
    ...EventPojoRecords.MOUSE_OUT,
    origin: undefined,
    config: defaultTimedEventConfigRecord,
    target: defaultTargetElementRecord,
    targets: List([defaultTargetElementRecord]),
  },
  'MouseHoverOutEventRecord'
);
const ScrollIntoViewEventRecord = Record(
  {
    ...EventPojoRecords.SCROLL_INTO_VIEW,
    origin: undefined,
    config: defaultTimedScrollEventConfigRecord,
    target: defaultTargetElementRecord,
    targets: List([defaultTargetElementRecord]),
  },
  'ScrollIntoViewEventRecord'
);
const ScrollOutOfViewEventRecord = Record(
  {
    ...EventPojoRecords.SCROLL_OUT_OF_VIEW,
    origin: undefined,
    config: defaultTimedScrollEventConfigRecord,
    target: defaultTargetElementRecord,
    targets: List([defaultTargetElementRecord]),
  },
  'ScrollOutOfViewEventRecord'
);

/**
 * Timed Page Events Immutable
 */

const PageLoadStartEventRecord = Record(
  {
    ...EventPojoRecords.PAGE_START,
    origin: undefined,
    config: defaultTimedEventConfigRecord,
    target: defaultTargetPageRecord,
    targets: List([defaultTargetPageRecord]),
  },
  'PageLoadStartEventRecord'
);
const PageLoadFinishEventRecord = Record(
  {
    ...EventPojoRecords.PAGE_FINISH,
    origin: undefined,
    config: defaultTimedEventConfigRecord,
    target: defaultTargetPageRecord,
    targets: List([defaultTargetPageRecord]),
  },
  'PageLoadFinishEventRecord'
);
const PageScrolledUpEventRecord = Record(
  {
    ...EventPojoRecords.PAGE_SCROLL_UP,
    origin: undefined,
    config: defaultTimedScrollEventConfigRecord,
    target: defaultTargetPageRecord,
    targets: List([defaultTargetPageRecord]),
  },
  'PageScrolledUpEventRecord'
);
const PageScrolledDownEventRecord = Record(
  {
    ...EventPojoRecords.PAGE_SCROLL_DOWN,
    origin: undefined,
    config: defaultTimedScrollEventConfigRecord,
    target: defaultTargetPageRecord,
    targets: List([defaultTargetPageRecord]),
  },
  'PageScrolledDownEventRecord'
);

/**
 * Continuous Mouse Events Immutable
 */

const MouseMoveInElementEventRecord = Record(
  {
    ...EventPojoRecords.MOUSE_MOVE,
    origin: undefined,
    config: List([
      EventConfigRecordConsts.CONTINUOUS.MOUSE.ELEMENT.X_AXIS(),
      EventConfigRecordConsts.CONTINUOUS.MOUSE.ELEMENT.Y_AXIS(),
    ]),
    target: defaultTargetElementRecord,
    targets: List([defaultTargetElementRecord]),
  },
  'MouseMoveInElementEventRecord'
);
const ScrollingInViewEventRecord = Record(
  {
    ...EventPojoRecords.SCROLLING_IN_VIEW,
    origin: undefined,
    config: List([EventConfigRecordConsts.CONTINUOUS.SCROLL()]),
    target: defaultTargetElementRecord,
    targets: List([defaultTargetElementRecord]),
  },
  'ScrollingInViewEventRecord'
);

/**
 * Continuous Page Events Immutable
 */

const MouseMoveInViewportEventRecord = Record(
  {
    ...EventPojoRecords.MOUSE_MOVE_IN_VIEWPORT,
    origin: undefined,
    config: List([
      EventConfigRecordConsts.CONTINUOUS.MOUSE.VIEWPORT.X_AXIS(),
      EventConfigRecordConsts.CONTINUOUS.MOUSE.VIEWPORT.Y_AXIS(),
    ]),
    target: defaultTargetPageRecord,
    targets: List([defaultTargetPageRecord]),
  },
  'MouseMoveInViewportEventRecord'
);
const WhilePageIsScrollingEventRecord = Record(
  {
    ...EventPojoRecords.PAGE_SCROLL,
    origin: undefined,
    config: List([EventConfigRecordConsts.CONTINUOUS.SCROLL()]),
    target: defaultTargetPageRecord,
    targets: List([defaultTargetPageRecord]),
  },
  'WhilePageIsScrollingEventRecord'
);

export const EventRecords = {
  [EventTypeConsts.DROPDOWN_OPEN]: DropdownOpenEventRecord,
  [EventTypeConsts.DROPDOWN_CLOSE]: DropdownCloseEventRecord,
  [EventTypeConsts.NAVBAR_OPEN]: NavbarOpenEventRecord,
  [EventTypeConsts.NAVBAR_CLOSE]: NavbarCloseEventRecord,
  [EventTypeConsts.SLIDER_ACTIVE]: SliderActiveEventRecord,
  [EventTypeConsts.SLIDER_INACTIVE]: SliderInactiveEventRecord,
  [EventTypeConsts.TAB_ACTIVE]: TabActiveEventRecord,
  [EventTypeConsts.TAB_INACTIVE]: TabInactiveEventRecord,
  [EventTypeConsts.MOUSE_CLICK]: MouseClickFirstEventRecord,
  [EventTypeConsts.MOUSE_SECOND_CLICK]: MouseClickSecondEventRecord,
  [EventTypeConsts.MOUSE_OVER]: MouseHoverOverEventRecord,
  [EventTypeConsts.MOUSE_OUT]: MouseHoverOutEventRecord,
  [EventTypeConsts.SCROLL_INTO_VIEW]: ScrollIntoViewEventRecord,
  [EventTypeConsts.SCROLL_OUT_OF_VIEW]: ScrollOutOfViewEventRecord,
  [EventTypeConsts.PAGE_START]: PageLoadStartEventRecord,
  [EventTypeConsts.PAGE_FINISH]: PageLoadFinishEventRecord,
  [EventTypeConsts.PAGE_SCROLL_UP]: PageScrolledUpEventRecord,
  [EventTypeConsts.PAGE_SCROLL_DOWN]: PageScrolledDownEventRecord,
  [EventTypeConsts.MOUSE_MOVE]: MouseMoveInElementEventRecord,
  [EventTypeConsts.SCROLLING_IN_VIEW]: ScrollingInViewEventRecord,
  [EventTypeConsts.MOUSE_MOVE_IN_VIEWPORT]: MouseMoveInViewportEventRecord,
  [EventTypeConsts.PAGE_SCROLL]: WhilePageIsScrollingEventRecord,
} as const;

export type PageEventRecordType =
  | ReturnType<typeof PageLoadStartEventRecord>
  | ReturnType<typeof PageLoadFinishEventRecord>
  | ReturnType<typeof PageScrolledUpEventRecord>
  | ReturnType<typeof PageScrolledDownEventRecord>
  | ReturnType<typeof MouseMoveInViewportEventRecord>
  | ReturnType<typeof WhilePageIsScrollingEventRecord>;

export type ElementEventRecordType =
  | ReturnType<typeof DropdownOpenEventRecord>
  | ReturnType<typeof DropdownCloseEventRecord>
  | ReturnType<typeof NavbarOpenEventRecord>
  | ReturnType<typeof NavbarCloseEventRecord>
  | ReturnType<typeof SliderActiveEventRecord>
  | ReturnType<typeof SliderInactiveEventRecord>
  | ReturnType<typeof TabActiveEventRecord>
  | ReturnType<typeof TabInactiveEventRecord>
  | ReturnType<typeof MouseClickFirstEventRecord>
  | ReturnType<typeof MouseClickSecondEventRecord>
  | ReturnType<typeof MouseHoverOverEventRecord>
  | ReturnType<typeof MouseHoverOutEventRecord>
  | ReturnType<typeof ScrollIntoViewEventRecord>
  | ReturnType<typeof ScrollOutOfViewEventRecord>
  | ReturnType<typeof MouseMoveInElementEventRecord>
  | ReturnType<typeof ScrollingInViewEventRecord>;

export type EventRecordType = ElementEventRecordType | PageEventRecordType;
export type EventTRecordypes = List<EventRecordType>;
